/**
 * Organigramm — Graphical org chart showing department hierarchy.
 *
 * @page /organigramm
 * @roles admin, manager
 * @api GET /api/organigramm
 * @i18n organigramm
 * @since v2.332.0
 */
import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Building2, Users, User, ChevronDown, ChevronRight, LayoutGrid, Warehouse, BellRing } from 'lucide-react';
import MitarbeiterListItem from '../components/MitarbeiterListItem';
import NotificationEmpfaengerModal from '../components/NotificationEmpfaengerModal';

const API = import.meta.env.VITE_API_URL || '';

const VERWALTER_ROLLEN = ['administrator', 'ops_manager', 'cs_teamlead', 'teamleiter'];

export default function OrganigrammPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [empfCtx, setEmpfCtx] = useState(null); // null | { abteilungId, label }
  const darfVerwalten = VERWALTER_ROLLEN.includes(user?.rolle);

  useEffect(() => {
    fetch(`${API}/api/organigramm`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setData(d);
        // Expand all by default
        const exp = {};
        [...(d.buero || []), ...(d.lager || [])].forEach(a => { exp[a.id] = true; });
        setExpanded(exp);
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div className="p-8 text-center text-[var(--text-tertiary)]">{t('common.loading')}</div>;
  if (!data) return null;

  const DeptCard = ({ dept }) => {
    const isOpen = expanded[dept.id];
    return (
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden bg-[var(--bg-card,#fff)]">
        {/* Header — Toggle + (gated) Abteilungs-Empfaenger-Button als Geschwister, kein Button-im-Button (#1561) */}
        <div className="flex items-center hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
          <button
            onClick={() => toggleExpand(dept.id)}
            className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left"
          >
            {isOpen ? <ChevronDown size={16} className="text-[var(--text-tertiary)]" /> : <ChevronRight size={16} className="text-[var(--text-tertiary)]" />}
            <Building2 size={18} className="text-[#338edc] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[var(--text-primary)] truncate">{dept.name}</div>
              {dept.vorgesetzter_name && (
                <div className="text-xs text-[var(--text-secondary)] truncate">{dept.vorgesetzter_name}</div>
              )}
            </div>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 shrink-0">
              {dept.mitarbeiter.length}
            </span>
          </button>
          {darfVerwalten && (
            <button
              onClick={() => setEmpfCtx({ abteilungId: dept.id, label: dept.name })}
              title={t('notifEmpf.scopeDept', 'Benachrichtigungs-Empfänger dieser Abteilung')}
              className="shrink-0 px-3 py-3 text-[var(--text-tertiary)] hover:text-[#338edc] transition-colors"
            >
              <BellRing size={16} />
            </button>
          )}
        </div>

        {/* Employees */}
        {isOpen && dept.mitarbeiter.length > 0 && (
          <div className="border-t border-[var(--border-default,#E5E7EB)] divide-y divide-[var(--border-table,#F3F4F6)]">
            {dept.mitarbeiter.map(m => (
              <a
                key={m.id}
                href={`/mitarbeiter/${m.id}`}
                className="block px-4 py-2.5 hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
              >
                <MitarbeiterListItem mitarbeiter={m} size="sm" apiBase={API} />
              </a>
            ))}
          </div>
        )}

        {isOpen && dept.mitarbeiter.length === 0 && (
          <div className="border-t border-[var(--border-default,#E5E7EB)] px-4 py-4 text-center text-xs text-[var(--text-tertiary)]">
            {t('organigramm.empty')}
          </div>
        )}
      </div>
    );
  };

  const BereichSection = ({ title, depts, icon }) => (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h2 className="text-base font-semibold text-[var(--text-primary)]">{title}</h2>
        <span className="text-xs text-[var(--text-tertiary)]">({depts.length} {t('organigramm.departments')})</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {depts.map(d => <DeptCard key={d.id} dept={d} />)}
      </div>
    </div>
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/10 flex items-center justify-center">
            <Users size={20} className="text-[#8B5CF6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">{t('organigramm.title')}</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{t('organigramm.subtitle')}</p>
          </div>
        </div>
        {darfVerwalten && (
          <button
            onClick={() => setEmpfCtx({ abteilungId: null, label: t('notifEmpf.scopeGlobalLabel', 'Global (alle Abteilungen)') })}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors shrink-0"
          >
            <BellRing size={16} className="text-[#338edc]" />
            {t('notifEmpf.title', 'Benachrichtigungs-Empfänger verwalten')}
          </button>
        )}
      </div>

      {empfCtx && <NotificationEmpfaengerModal context={empfCtx} onClose={() => setEmpfCtx(null)} />}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <LayoutGrid size={16} className="text-[#8B5CF6]" />
            <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('organigramm.departments')}</p>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{data.gesamt_abteilungen}</p>
        </div>
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Users size={16} className="text-[#8B5CF6]" />
            <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('organigramm.employees')}</p>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{data.gesamt_mitarbeiter}</p>
        </div>
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={16} className="text-[#338edc]" />
            <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('organigramm.office')}</p>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{data.buero.length}</p>
        </div>
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <Warehouse size={16} className="text-[#338edc]" />
            <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('organigramm.warehouse')}</p>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)]">{data.lager.length}</p>
        </div>
      </div>

      {/* Bereiche */}
      <div className="space-y-8">
        <BereichSection
          title={t('organigramm.office')}
          depts={data.buero}
          icon={<Building2 size={20} className="text-[#338edc]" />}
        />
        <BereichSection
          title={t('organigramm.warehouse')}
          depts={data.lager}
          icon={<Building2 size={20} className="text-[#6366F1]" />}
        />
      </div>
    </div>
  );
}
